// Phase I5.5 — Direction B (Split Pane).
//
// Two-column layout: left rail aggregates the verdict + tier + sharp-money
// summary; the right pane scrolls the Intelligence Stack, DNA panels,
// Scoreline Matrix, Adversarial Review, Agent Debate, and Build-Your-Own.
// Composes the same panels Direction A uses (no new hooks, no fabricated
// data). Empty/loading paths mirror Direction A's copy.

import { useMemo, useState } from "react";
import { useConversionTriggerPump } from "../../free-tier/useConversionTriggerPump.ts";
import { useSuperAccaToday } from "../../super-acca/useSuperAccaToday.ts";
import { AdversarialReview } from "./panels/AdversarialReview.tsx";
import { AgentDebate } from "./panels/AgentDebate.tsx";
import { BuildYourOwn } from "./panels/BuildYourOwn.tsx";
import { CornersIntelPanel } from "./panels/CornersIntelPanel.tsx";
import { IntelligenceStack } from "./panels/IntelligenceStack.tsx";
import { IntelligenceVerdict } from "./panels/IntelligenceVerdict.tsx";
import { MarketCategorySelector } from "./panels/MarketCategorySelector.tsx";
import type { MarketCategoryFilter } from "./panels/MarketCategorySelector.tsx";
import { MatchCommandHeader } from "./panels/MatchCommandHeader.tsx";
import { PlayerDNAPanel } from "./panels/PlayerDNAPanel.tsx";
import { RefereeDNAPanel } from "./panels/RefereeDNAPanel.tsx";
import { ScorelineMatrix } from "./panels/ScorelineMatrix.tsx";
import { TierStrip } from "./panels/TierStrip.tsx";

const eyebrow: React.CSSProperties = {
  fontFamily: "var(--sa-font-mono)",
  fontSize: 10.5,
  letterSpacing: "0.22em",
  textTransform: "uppercase",
  color: "var(--sa-accent-saffron)",
};

export function SuperAccaPageDirectionB(): JSX.Element {
  const vm = useSuperAccaToday();
  const {
    slip,
    fixture,
    disciplinary,
    territorial,
    playerPerformance,
    scorelineMatrix,
    derivedMarkets,
    sharpMoney,
    loading,
    error,
  } = vm;

  const [category, setCategory] = useState<MarketCategoryFilter>("ALL");

  useConversionTriggerPump(
    slip ? "super_acca_view" : null,
    slip ? { slip_id: slip.id, pick_date: slip.pick_date } : {},
  );

  const publishedLabel =
    slip?.status === "published"
      ? "PUBLISHED · 11:30 GMT"
      : slip?.status === "abstained"
        ? "ABSTAINED · TIER FLOOR NOT MET"
        : "AWAITING TODAY'S BUILD";

  const tallies = useMemo(() => {
    if (!slip) return null;
    const t: Record<string, number> = {
      OUTCOME: 0,
      DISCIPLINE: 0,
      TERRITORY: 0,
      PLAYER: 0,
    };
    for (const leg of slip.legs) {
      if (leg.category) t[leg.category] = (t[leg.category] ?? 0) + 1;
    }
    return t;
  }, [slip]);

  return (
    <div
      className="sa-root"
      data-screen-label="00 SUPER ACCA · B"
      style={{
        background: "var(--sa-bg-base)",
        minHeight: "var(--vh-full)",
        color: "var(--sa-fg-primary)",
        fontFamily: "var(--sa-font-body)",
      }}
    >
      <MatchCommandHeader
        fixture={fixture}
        sharp={sharpMoney}
        publishedLabel={publishedLabel}
      />

      <div style={{ padding: "12px 40px 0", maxWidth: 1320, margin: "0 auto" }}>
        <span style={eyebrow}>Direction B · Split Pane</span>
      </div>

      {error ? (
        <div
          style={{
            margin: "16px 40px 0",
            padding: 14,
            border: "1px solid var(--sa-signal-red)",
            background: "var(--sa-signal-red-dim)",
            color: "var(--sa-signal-red)",
            borderRadius: 4,
            fontFamily: "var(--sa-font-mono)",
            fontSize: 12,
          }}
        >
          <strong>Could not load Super Acca.</strong> {error}
        </div>
      ) : null}

      {loading ? (
        <div
          style={{
            margin: "24px 40px",
            padding: 40,
            border: "1px solid var(--sa-hairline)",
            background: "var(--sa-bg-panel)",
            borderRadius: 8,
            fontFamily: "var(--sa-font-mono)",
            fontSize: 12,
            color: "var(--sa-fg-secondary)",
            letterSpacing: "0.16em",
          }}
        >
          LOADING TODAY'S COMMAND CENTRE…
        </div>
      ) : !slip ? (
        <EmptyB />
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(320px, 380px) 1fr",
            gap: 0,
            borderTop: "1px solid var(--sa-hairline)",
            maxWidth: 1320,
            margin: "16px auto 0",
          }}
        >
          {/* LEFT — sticky verdict rail */}
          <aside
            style={{
              background: "var(--sa-bg-panel)",
              borderRight: "1px solid var(--sa-hairline)",
              padding: 22,
              alignSelf: "start",
              position: "sticky",
              top: 0,
            }}
          >
            <span style={eyebrow}>Verdict</span>
            <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 14 }}>
              <TierStrip slip={slip} />
              <IntelligenceVerdict slip={slip} />
            </div>

            <div
              style={{
                marginTop: 18,
                padding: 14,
                background: "var(--sa-bg-base)",
                border: "1px solid var(--sa-hairline)",
                borderRadius: 4,
              }}
            >
              <div
                style={{
                  fontFamily: "var(--sa-font-mono)",
                  fontSize: 10,
                  color: "var(--sa-fg-muted)",
                  letterSpacing: "0.18em",
                }}
              >
                LINE MOVE
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "baseline",
                  gap: 8,
                  marginTop: 8,
                  fontFamily: "var(--sa-font-mono)",
                  fontSize: 14,
                }}
              >
                <span
                  style={{
                    color: "var(--sa-fg-muted)",
                    textDecoration: sharpMoney.open != null ? "line-through" : "none",
                  }}
                >
                  {sharpMoney.open != null ? sharpMoney.open.toFixed(2) : "—"}
                </span>
                <span style={{ color: "var(--sa-accent-saffron)" }}>→</span>
                <span style={{ fontSize: 18, color: "var(--sa-accent-saffron)" }}>
                  {sharpMoney.now != null ? sharpMoney.now.toFixed(2) : "—"}
                </span>
                {sharpMoney.signal ? (
                  <span
                    style={{
                      marginLeft: "auto",
                      fontSize: 10,
                      color: "var(--sa-fg-secondary)",
                      letterSpacing: "0.16em",
                    }}
                  >
                    {sharpMoney.signal}
                  </span>
                ) : null}
              </div>
            </div>

            <div style={{ marginTop: 22 }}>
              <span style={eyebrow}>Same-Game Markets</span>
              <div
                style={{
                  marginTop: 12,
                  display: "flex",
                  flexDirection: "column",
                  gap: 6,
                }}
              >
                {(["OUTCOME", "DISCIPLINE", "TERRITORY", "PLAYER"] as const).map((id) => (
                  <div
                    key={id}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      padding: "8px 12px",
                      border: "1px solid var(--sa-hairline)",
                      borderRadius: 4,
                      fontFamily: "var(--sa-font-mono)",
                      fontSize: 11,
                      letterSpacing: "0.1em",
                      color: "var(--sa-fg-secondary)",
                    }}
                  >
                    <span>{id}</span>
                    <span>{tallies?.[id] ?? 0}</span>
                  </div>
                ))}
              </div>
            </div>
          </aside>

          {/* RIGHT — scrollable stack */}
          <div style={{ padding: "26px 28px 60px", minWidth: 0 }}>
            <IntelligenceStack slip={slip} />

            <div style={{ marginTop: 24 }}>
              <MarketCategorySelector active={category} onChange={setCategory} />
            </div>
            <div style={{ marginTop: 18 }}>
              {category === "DISCIPLINE" ? <RefereeDNAPanel data={disciplinary} /> : null}
              {category === "TERRITORY" ? <CornersIntelPanel data={territorial} /> : null}
              {category === "PLAYER" ? <PlayerDNAPanel data={playerPerformance} /> : null}
            </div>

            <ScorelineMatrix matrix={scorelineMatrix} derived={derivedMarkets} />
            <AdversarialReview slip={slip} />
            <AgentDebate slip={slip} />
            <BuildYourOwn slip={slip} />
          </div>
        </div>
      )}

      <footer
        style={{
          marginTop: 40,
          padding: "16px 40px",
          borderTop: "1px solid var(--sa-hairline)",
          fontFamily: "var(--sa-font-mono)",
          fontSize: 11,
          color: "var(--sa-fg-muted)",
          letterSpacing: "0.16em",
          textAlign: "center",
        }}
      >
        STRIKZA NEVER PLACES BETS · YOU COPY ANY SLIP TO YOUR BOOKMAKER MANUALLY
      </footer>
    </div>
  );
}

function EmptyB() {
  return (
    <div
      style={{
        margin: "24px 40px",
        padding: 40,
        border: "1px solid var(--sa-hairline)",
        background: "var(--sa-bg-panel)",
        borderRadius: 8,
      }}
    >
      <div style={eyebrow}>Direction B · Today's Build</div>
      <h2
        style={{
          fontFamily: "var(--sa-font-display)",
          fontSize: 28,
          fontWeight: 600,
          marginTop: 12,
          color: "var(--sa-fg-primary)",
        }}
      >
        Today's slip is in adversarial review
      </h2>
      <p
        style={{
          fontFamily: "var(--sa-font-body)",
          fontSize: 14,
          color: "var(--sa-fg-secondary)",
          lineHeight: 1.6,
          marginTop: 12,
          maxWidth: 720,
        }}
      >
        The Super Acca only publishes once the slip clears the contradiction
        engine, the adversarial review, and meets the tier confidence floor.
        If conviction isn't there by 11:30 GMT, no slip is published —
        STRIKZA never invents a fabricated tip. Check back later today, or
        review yesterday's settled slip in your archive.
      </p>
    </div>
  );
}
